import React, { useState } from 'react'
import { Box, Checkbox, Text, useDisclosure } from '@chakra-ui/react'
import { FaClipboardList } from 'react-icons/fa6'

import MenuItem from '~components/shared/menu/menu-item'
import SharedModal from '~components/shared/modals/shared-modal'
import { LocalStorageKeys } from '~config/constants'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { useExportMessages } from '../../export-messages-context'

interface Props {
    showMessagesForAllLocations: boolean
    setShowMessagesForAllLocations: (show: boolean) => void
}

/**
 * Persists the "skip warning" flag directly to localStorage. We can't rely on
 * `useLocalStorage`'s effect to do this because confirming the modal triggers
 * `setIsExporting(true)`, which unmounts the menu (and therefore this
 * component) in the same render. The pending state update is discarded with
 * the fiber and the effect never gets a chance to write the value.
 */
function setSkipExportAllLocationsWarning(value: boolean) {
    try {
        if (typeof window === 'undefined') return
        window.localStorage.setItem(
            LocalStorageKeys.exportMessagesAllLocationsWarningDontShowAgain,
            JSON.stringify(value),
        )
    } catch (error) {
        console.error(
            `Failed to persist "${LocalStorageKeys.exportMessagesAllLocationsWarningDontShowAgain}":`,
            error,
        )
    }
}

export default function ExportMessagesAction({
    showMessagesForAllLocations,
    setShowMessagesForAllLocations,
}: Props) {
    const { setIsExporting } = useExportMessages()
    const warningModalDisclosure = useDisclosure()
    const [skipWarning, setSkipWarning] = useLocalStorage<boolean>(
        LocalStorageKeys.exportMessagesAllLocationsWarningDontShowAgain,
        false,
    )

    function startExportingForCurrentLocation() {
        if (showMessagesForAllLocations) {
            setShowMessagesForAllLocations(false)
        }
        setIsExporting(true)
    }

    function handleClick() {
        if (!showMessagesForAllLocations || skipWarning) {
            startExportingForCurrentLocation()
            return
        }

        warningModalDisclosure.onOpen()
    }

    function handleConfirmWarning(dontShowAgain: boolean) {
        if (dontShowAgain) {
            // `setIsExporting(true)` unmounts this component (the More Options
            // menu disappears while exporting), so `useLocalStorage`'s effect
            // never runs to persist the new value. Write synchronously here
            // and also call the setter so the in-memory state stays in sync
            // if the component happens to remain mounted.
            setSkipWarning(true)
            setSkipExportAllLocationsWarning(true)
        }
        startExportingForCurrentLocation()
    }

    return (
        <>
            <MenuItem icon={FaClipboardList} onClick={handleClick}>
                Select Message to Export
            </MenuItem>
            <AllMessagesWarningModal
                isOpen={warningModalDisclosure.isOpen}
                onClose={warningModalDisclosure.onClose}
                onConfirm={handleConfirmWarning}
            />
        </>
    )
}

interface AllMessagesWarningModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: (dontShowAgain: boolean) => void
}

function AllMessagesWarningModal({
    isOpen,
    onClose,
    onConfirm,
}: AllMessagesWarningModalProps) {
    const [dontShowAgain, setDontShowAgain] = useState(false)

    function handleClose() {
        setDontShowAgain(false)
        onClose()
    }

    function handleConfirm() {
        onConfirm(dontShowAgain)
        setDontShowAgain(false)
    }

    return (
        <SharedModal
            size="md"
            isOpen={isOpen}
            onClose={handleClose}
            title="Exporting only includes the current location"
            onCancel={handleClose}
            cancelText="Cancel"
            onConfirm={handleConfirm}
            confirmText="Continue"
        >
            <Box>
                <Text mb={4}>
                    Messages can only be exported from the current location.
                    Continuing will turn off "Show Messages for All Locations"
                    so you can select messages to export.
                </Text>
                <Checkbox
                    colorScheme="brand"
                    isChecked={dontShowAgain}
                    onChange={e => setDontShowAgain(e.target.checked)}
                >
                    <Text fontWeight="medium" fontSize="sm">
                        Don't show this again
                    </Text>
                </Checkbox>
            </Box>
        </SharedModal>
    )
}
